import React, { useContext, useState, useEffect, useCallback } from "react";
import { TranslatorContext } from "../../context/Translator";
import { useLocation, useNavigate, useParams, Link } from "react-router-dom";
import { Row, Col, Card, Button, Badge, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../layouts/PageLayout";

export default function MplacerProductDetailPage() {
    const { t } = useContext(TranslatorContext);
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();

    const [product, setProduct] = useState(
        location.state?.item || location.state?.product || null
    );
    const [loading, setLoading] = useState(!product);
    const [error, setError] = useState(null);
    const [selectedImage, setSelectedImage] = useState("");

    const fetchProductById = useCallback(async () => {
        if (!id) return;
        setLoading(true);
        setError(null);

        try {
            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}/market-placer/products-market-places/${id}`,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            const resData = response.data;
            if (resData?.success && resData?.data) {
                const item = Array.isArray(resData.data) ? resData.data[0] : resData.data;
                setProduct(item);
            } else {
                setError(resData?.message || "Failed to fetch product details.");
            }
        } catch (err) {
            console.error("Error fetching product details:", err);
            setError(
                err.response?.data?.message || "Failed to load product details."
            );
        } finally {
            setLoading(false);
        }
    }, [id]);

    useEffect(() => {
        if (!product && id) {
            fetchProductById();
        }
    }, [id, product, fetchProductById]);

    useEffect(() => {
        if (product) {
            const mainImg = product.first_img || product.card_img || product.image_1 || product.image_2;
            if (mainImg) {
                setSelectedImage(`${process.env.REACT_APP_IMG_URL}${mainImg}`);
            }
        }
    }, [product]);

    const decodeEntities = (html) => {
        if (!html) return "";
        const txt = document.createElement("textarea");
        txt.innerHTML = html;
        return txt.value;
    };

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2 text-muted">{t("loading_product_details...")}</p>
                </div>
            </PageLayout>
        );
    }

    if (error || !product) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Alert variant="danger" className="d-inline-block px-5 shadow-sm">
                        {error || t("no product data available")}
                    </Alert>
                    <div>
                        <Button variant="primary" className="mt-3" onClick={() => navigate(-1)}>
                            {t("back to list")}
                        </Button>
                    </div>
                </div>
            </PageLayout>
        );
    }

    // Gallery images collection including image_1 & image_2
    const galleryImages = [
        ...(product.first_img ? [product.first_img] : []),
        ...(product.card_img ? [product.card_img] : []),
        ...(product.image_1 ? [product.image_1] : []),
        ...(product.image_2 ? [product.image_2] : []),
        ...(product.images ? product.images.map((img) => img.image) : []),
    ];

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="mc-card mb-4 border-0 shadow-sm">
                        <Card.Body className="mc-breadcrumb d-flex justify-content-between align-items-center py-3">
                            <div>
                                <h3 className="mc-breadcrumb-title mb-1 fw-bold">{t("Product Detail")}</h3>
                                <ul className="mc-breadcrumb-list d-flex gap-2 p-0 m-0 list-unstyled small text-muted">
                                    <li className="mc-breadcrumb-item">
                                        <Link to="#" className="mc-breadcrumb-link text-decoration-none text-muted">{t("Home")}</Link>
                                    </li>
                                    <li>/</li>
                                    <li className="mc-breadcrumb-item">
                                        <Link
                                            to="#"
                                            onClick={(e) => {
                                                e.preventDefault();
                                                navigate(-1);
                                            }}
                                            className="mc-breadcrumb-link text-decoration-none text-muted"
                                        >
                                            {t("Market Placer Products")}
                                        </Link>
                                    </li>
                                    <li>/</li>
                                    <li className="mc-breadcrumb-item text-primary fw-semibold">{t("Detail")}</li>
                                </ul>
                            </div>
                            <Button variant="outline-secondary" size="sm" onClick={() => navigate(-1)}>
                                {t("Back")}
                            </Button>
                        </Card.Body>
                    </Card>
                </Col>

                {/* Left Side: Product Image Gallery */}
                <Col lg={5}>
                    <Card className="mb-4 border-0 shadow-sm">
                        <Card.Body className="p-3">
                            <div className="text-center mb-3 bg-light rounded p-2" style={{ minHeight: "360px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                {selectedImage ? (
                                    <img
                                        src={selectedImage}
                                        alt={product.title}
                                        className="img-fluid rounded"
                                        style={{ maxHeight: "350px", objectFit: "contain", width: "100%" }}
                                    />
                                ) : (
                                    <span className="text-muted">{t("No Image Available")}</span>
                                )}
                            </div>

                            {galleryImages.length > 0 && (
                                <div className="d-flex gap-2 justify-content-center flex-wrap">
                                    {galleryImages.map((imgUrl, index) => {
                                        const fullUrl = `${process.env.REACT_APP_IMG_URL}${imgUrl}`;
                                        const isSelected = selectedImage === fullUrl;

                                        return (
                                            <div
                                                key={index}
                                                onClick={() => setSelectedImage(fullUrl)}
                                                className={`rounded p-1 border ${isSelected ? "border-primary bg-light" : "border-light"}`}
                                                style={{ cursor: "pointer", transition: "all 0.2s" }}
                                            >
                                                <img
                                                    src={fullUrl}
                                                    alt={`thumb-${index}`}
                                                    className="rounded"
                                                    style={{
                                                        width: "60px",
                                                        height: "60px",
                                                        objectFit: "cover",
                                                        opacity: isSelected ? 1 : 0.7,
                                                    }}
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </Col>

                {/* Right Side: Details & Stats */}
                <Col lg={7}>
                    <Card className="mb-4 border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom py-3">
                            <div className="d-flex justify-content-between align-items-start">
                                <div>
                                    {product.title && <h4 className="fw-bold mb-2 text-dark">{product.title}</h4>}
                                    <div className="d-flex gap-2 align-items-center">
                                        {product.product_code && (
                                            <Badge bg="primary" className="px-2 py-1 fs-6">
                                                {product.product_code}
                                            </Badge>
                                        )}
                                        {product.product_condition && (
                                            <Badge bg={product.product_condition === "used" ? "warning" : "success"} className="px-2 py-1 fs-6 text-uppercase">
                                                {product.product_condition}
                                            </Badge>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Card.Header>

                        <Card.Body className="p-4">
                            <Row className="g-3">
                                {product.category?.name && (
                                    <Col sm={6}>
                                        <span className="text-muted small d-block">{t("Category")}</span>
                                        <span className="fw-semibold text-dark">{product.category.name}</span>
                                    </Col>
                                )}

                                {product.brand?.name && (
                                    <Col sm={6}>
                                        <span className="text-muted small d-block">{t("Brand")}</span>
                                        <span className="fw-semibold text-dark">{product.brand.name}</span>
                                    </Col>
                                )}

                                {product.selling_style && (
                                    <Col sm={6}>
                                        <span className="text-muted small d-block">{t("Selling Style")}</span>
                                        <span className="fw-semibold text-dark">{product.selling_style}</span>
                                    </Col>
                                )}

                                {product.product_qty != null && (
                                    <Col sm={6}>
                                        <span className="text-muted small d-block">{t("Quantity")}</span>
                                        <span className="fw-semibold text-dark">{product.product_qty}</span>
                                    </Col>
                                )}

                                {product.buying_price != null && (
                                    <Col sm={4}>
                                        <span className="text-muted small d-block">{t("Buying Price")}</span>
                                        <span className="fs-5 text-primary fw-bold">${product.buying_price}</span>
                                    </Col>
                                )}

                                {product.sell_price_dollar != null && (
                                    <Col sm={4}>
                                        <span className="text-muted small d-block">{t("Sell Price Dollar")}</span>
                                        <span className="fs-5 text-success fw-bold">${product.sell_price_dollar}</span>
                                    </Col>
                                )}

                                {product.shipping_charges_dollar != null && (
                                    <Col sm={4}>
                                        <span className="text-muted small d-block">{t("Shipping Charges")}</span>
                                        <span className="fw-semibold text-dark">${product.shipping_charges_dollar}</span>
                                    </Col>
                                )}

                                {/* Delivery Details */}
                                {product.delivery && (product.delivery.courier_company?.name || product.delivery.tracking_number || product.delivery.status || product.delivery.delivery_days) && (
                                    <Col sm={12} className="mt-3">
                                        <span className="text-muted small d-block mb-1">{t("Delivery Details")}</span>
                                        <div className="p-3 bg-light rounded border-0">
                                            <Row className="g-2 small">
                                                {product.delivery.courier_company?.name && (
                                                    <Col sm={6}>
                                                        <strong>Courier: </strong>{product.delivery.courier_company.name}
                                                    </Col>
                                                )}
                                                {product.delivery.tracking_number && (
                                                    <Col sm={6}>
                                                        <strong>Tracking #: </strong>{product.delivery.tracking_number}
                                                    </Col>
                                                )}
                                                {product.delivery.status && (
                                                    <Col sm={6}>
                                                        <strong>Status: </strong><Badge bg="info" className="ms-1">{product.delivery.status}</Badge>
                                                    </Col>
                                                )}
                                                {product.delivery.delivery_days && (
                                                    <Col sm={6}>
                                                        <strong>Delivery Days: </strong>{product.delivery.delivery_days}
                                                    </Col>
                                                )}
                                            </Row>
                                        </div>
                                    </Col>
                                )}

                                {/* Order Timeline */}
                                {product.dates && (product.dates.ordered_at || product.dates.processing_at || product.dates.posted_at) && (
                                    <Col sm={12} className="mt-3">
                                        <span className="text-muted small d-block mb-1">{t("Order Timeline")}</span>
                                        <div className="p-3 bg-light rounded border-0">
                                            <Row className="g-2 small">
                                                {product.dates.ordered_at && (
                                                    <Col sm={4}>
                                                        <strong>Ordered At: </strong>{new Date(product.dates.ordered_at).toLocaleString()}
                                                    </Col>
                                                )}
                                                {product.dates.processing_at && (
                                                    <Col sm={4}>
                                                        <strong>Processing At: </strong>{new Date(product.dates.processing_at).toLocaleString()}
                                                    </Col>
                                                )}
                                                {product.dates.posted_at && (
                                                    <Col sm={4}>
                                                        <strong>Posted At: </strong>{new Date(product.dates.posted_at).toLocaleString()}
                                                    </Col>
                                                )}
                                            </Row>
                                        </div>
                                    </Col>
                                )}

                                {/* Payment Details */}
                                {product.payment && (product.payment.status || product.payment.date || product.payment.message) && (
                                    <Col sm={12} className="mt-3">
                                        <span className="text-muted small d-block mb-1">{t("Payment Details")}</span>
                                        <div className="p-3 bg-light rounded border-0">
                                            <Row className="g-2 small">
                                                {product.payment.status && (
                                                    <Col sm={4}>
                                                        <strong>Status: </strong>
                                                        <Badge bg={product.payment.status === "Paid" ? "success" : "warning"} className="ms-1">
                                                            {product.payment.status}
                                                        </Badge>
                                                    </Col>
                                                )}
                                                {product.payment.date && (
                                                    <Col sm={4}>
                                                        <strong>Date: </strong>{new Date(product.payment.date).toLocaleDateString()}
                                                    </Col>
                                                )}
                                                {product.payment.message && (
                                                    <Col sm={4}>
                                                        <strong>Message: </strong>{product.payment.message}
                                                    </Col>
                                                )}
                                            </Row>
                                        </div>
                                    </Col>
                                )}

                                {/* Short Description */}
                                {product.short_description && (
                                    <Col sm={12} className="mt-3">
                                        <span className="text-muted small d-block mb-1">{t("Short Description")}</span>
                                        <p className="text-secondary bg-light p-3 rounded mb-0 small lh-base">{product.short_description}</p>
                                    </Col>
                                )}

                                {/* Marketplaces */}
                                {product.marketPlace && product.marketPlace.length > 0 && (
                                    <Col sm={12} className="mt-3">
                                        <span className="text-muted small d-block mb-2">{t("Marketplaces")}</span>
                                        <div className="d-flex align-items-center gap-2 flex-wrap">
                                            {product.marketPlace.map((mp, i) => {
                                                const isSold = mp?.showing === 0;
                                                const statusText = isSold ? "Sold" : "Unsold";
                                                const targetUrl = mp?.base_url && mp?.reference ? `${mp.base_url}${mp.reference}` : "#";

                                                return (
                                                    <a
                                                        key={i}
                                                        href={targetUrl}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={`border rounded p-2 d-inline-flex align-items-center gap-2 text-decoration-none shadow-sm ${isSold ? "bg-light opacity-75" : "bg-white"}`}
                                                    >
                                                        {mp?.logo && (
                                                            <img
                                                                src={process.env.REACT_APP_IMG_URL + mp.logo}
                                                                alt={mp?.name || "marketplace"}
                                                                style={{ width: "22px", height: "22px", objectFit: "contain" }}
                                                            />
                                                        )}
                                                        <div className="d-flex flex-column">
                                                            {mp?.name && <span className="fw-semibold small text-dark">{mp.name}</span>}
                                                            {mp?.reference && (
                                                                <span className="text-muted" style={{ fontSize: "10px" }}>
                                                                    Ref: {mp.reference}
                                                                </span>
                                                            )}
                                                        </div>
                                                        <span className={`badge ${isSold ? "bg-danger" : "bg-success"}`} style={{ fontSize: "10px" }}>
                                                            {statusText}
                                                        </span>
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    </Col>
                                )}

                                {/* Full Description */}
                                {product.description && (
                                    <Col sm={12} className="mt-3">
                                        <span className="text-muted small d-block mb-1">{t("Full Description")}</span>
                                        <div
                                            className="border p-3 rounded bg-white text-dark small"
                                            dangerouslySetInnerHTML={{
                                                __html: decodeEntities(product.description),
                                            }}
                                        />
                                    </Col>
                                )}
                            </Row>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}